import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { isUserAdmin } from "@/lib/admin-check";
import { providerForItemType, type ItemType } from "@/lib/providers";


export type DispatchResult = {
  id: string;
  floristOneOrderId: string;
  status: string;
  isMock: boolean;
  recipientName: string;
  arrangementName: string;
  deliveryDate: string;
};

export type FulfillmentOrderRow = {
  id: string;
  recipient_name: string;
  arrangement_name: string | null;
  florist_one_order_id: string | null;
  status: string;
  is_mock: boolean;
  delivery_date: string;
  card_message: string | null;
  created_at: string;
  is_subscription_drop: boolean | null;
  authorized_choice: string | null;
  task_status: string | null;
};

/**
 * Admin-only dispatch. Client UIs cannot trigger Florist One deliveries. 
 * only verified backend flows (Stripe payment_intent.succeeded webhook, the
 * Twilio inbound SMS webhook after a successful charge, or the T-3
 * auto-lock cron) may initiate a florist call.
 *
 * When invoked by an admin, this handler REQUIRES an existing
 * fulfillment_orders row that is already covered by payment. Either it has
 * a linked Stripe `payment_intent_id`, is tied to an active subscription, or
 * has been explicitly authorized via SMS. Without that proof, we return 403.
 */
export const dispatchFloristOneOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { taskId: string }) => {
    if (!data?.taskId || typeof data.taskId !== "string") {
      throw new Error("taskId required");
    }
    return data;
  })
  .handler(async ({ data, context }): Promise<DispatchResult> => {
    const { supabase, userId } = context;
    if (!(await isUserAdmin(supabase, userId))) {
      console.warn("[florist-one] unauthorized dispatch attempt", { userId, taskId: data.taskId });
      throw new Error("Forbidden");
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: task, error: tErr } = await supabaseAdmin
      .from("fulfillment_orders")
      .select(
        "id, user_id, subscription_id, tier, kind, item_type, fulfillment_provider, task_status, status, source_type, sms_auth_state, authorized_choice, charge_type, charge_amount_cents, stripe_payment_intent_id, recipient_name, recipient_address, recipient_city, recipient_state, recipient_zip, delivery_date, card_message, product_id, arrangement_name",
      )
      .eq("id", data.taskId)
      .maybeSingle();
    if (tErr || !task) throw new Error("Task not found");

    // Payment verification. A drop only leaves the building when money has
    // actually moved: an active subscription alone never covers a gift.
    const hasPaymentIntent = !!task.stripe_payment_intent_id;
    const creditCovered =
      task.charge_type === "credit" || task.charge_type === "subscription_credit";
    const isDispatched = task.status === "dispatched" || task.task_status === "dispatched";

    if (!hasPaymentIntent && !creditCovered && !isDispatched) {
      console.warn("[dispatch] payment gate failed", {
        userId,
        taskId: data.taskId,
        status: task.status,
        task_status: task.task_status,
        sms_auth_state: task.sms_auth_state,
        charge_type: task.charge_type,
      });
      throw new Error(
        "Forbidden: no verified payment on this task. Charge the card (autopilot lock or instant dispatch) before sending.",
      );
    }


    const itemType = ((task.item_type as string) ?? "FLOWERS") as ItemType;
    const provider = providerForItemType(itemType);

    if (itemType !== "DIGITAL_CARD") {
      if (!task.recipient_address || !task.recipient_city || !task.recipient_state || !task.recipient_zip) {
        throw new Error("Task is missing the recipient address");
      }
    }
    if (itemType === "FLOWERS" && !task.delivery_date) {
      throw new Error("Task is missing the delivery date");
    }

    const itemName =
      (task.arrangement_name as string) ??
      (itemType === "FLOWERS" ? "FLWR Signature Arrangement" : "FLWR Concierge Gift");

    const { dispatchReward } = await import("@/lib/dispatch.server");
    const result = await dispatchReward({
      senderUserId: (task.user_id as string | null) ?? null,
      itemType,
      recipientName: task.recipient_name ?? "Recipient",
      recipientAddress: (task.recipient_address as string | null) ?? null,
      recipientCity: (task.recipient_city as string | null) ?? null,
      recipientState: (task.recipient_state as string | null) ?? null,
      recipientZip: (task.recipient_zip as string | null) ?? null,
      deliveryDate: (task.delivery_date as string | null) ?? null,
      cardMessage: (task.card_message as string) ?? "With love, FLWR Club",
      productId: (task.product_id as string) ?? "PREMIUM100",
      itemName,
      amountCents: (task.charge_amount_cents as number | null) ?? 10000,
    });

    // What we pay the vendor is the catalog price of the item, never what the
    // member was charged. If we cannot resolve a real vendor price we record
    // none rather than an invented one.
    const catalogProductId = (task.product_id as string | null) ?? null;
    let vendorCost: number | null = null;
    if (catalogProductId) {
      const { data: priced } = await supabaseAdmin
        .from("products")
        .select("price_cents")
        .eq("external_product_id", catalogProductId)
        .gt("price_cents", 0)
        .maybeSingle();
      vendorCost = (priced?.price_cents as number | null) ?? null;
    }

    await supabaseAdmin
      .from("fulfillment_orders")
      .update({
        status: "dispatched",
        task_status: "dispatched",
        florist_one_order_id: result.confirmationNumber,
        is_mock: result.isMock,
        source_type: provider,
        fulfillment_provider: provider,
        fulfilled_at: new Date().toISOString(),
        fulfilled_by: userId,
        vendor_cost_cents: vendorCost,
        vendor_order_payload: {
          provider,
          itemType,
          productId: (task.product_id as string) ?? "PREMIUM100",
          itemName,
          amountCents: vendorCost,
          memberChargeCents: (task.charge_amount_cents as number | null) ?? null,
          deliveryDate: (task.delivery_date as string | null) ?? null,
        },
        vendor_response: ((result as { rawResponse?: unknown }).rawResponse ?? {
          confirmationNumber: result.confirmationNumber,
          isMock: result.isMock,
        }) as never,
        vendor_placed_at: new Date().toISOString(),
      })
      .eq("id", task.id);

    const { sendOrderConfirmationEmail } = await import("@/lib/order-confirmation.server");
    await sendOrderConfirmationEmail(task.id as string);




    return {
      id: task.id as string,
      floristOneOrderId: result.confirmationNumber,
      status: "dispatched",
      isMock: result.isMock,
      recipientName: task.recipient_name ?? "",
      arrangementName: (task.arrangement_name as string) ?? "",
      deliveryDate: task.delivery_date as string,
    };
  });

export const listFulfillmentOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<FulfillmentOrderRow[]> => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("fulfillment_orders")
      .select(
        "id, recipient_name, arrangement_name, florist_one_order_id, status, is_mock, delivery_date, card_message, created_at, is_subscription_drop, authorized_choice, task_status",
      )
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      console.error("[florist-one] failed to list fulfillment orders", error);
      throw new Error("Failed to load orders");
    }
    return (data ?? []) as FulfillmentOrderRow[];
  });
